import type { ChangeEvent, Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../lib/format';

export interface PeoplePosition {
  id: number;
  job_code: string;
  title: string;
  grade?: string | null;
  department_name?: string | null;
  min_salary: number;
  max_salary: number;
  headcount: number;
  filled_headcount: number;
}

export interface PeopleDepartment {
  id: number | string;
  name: string;
}

export interface PeopleEmployee {
  id: number | string;
  full_name: string;
  employee_code: string;
  department?: string | null;
  basic_salary: number;
  taxable_allowance: number;
  payout_method?: string | null;
}

export interface PeopleTimesheet {
  id: number | string;
  work_date: string;
  regular_hours: number | string;
  overtime_hours: number | string;
  status: string;
}

export interface EmployeeFormState {
  full_name: string;
  employee_code: string;
  basic_salary: string | number;
  taxable_allowance: string | number;
  non_taxable_allowance: string | number;
  other_deductions: string | number;
  department: string;
  position: string;
  position_id: string;
  tax_id: string;
  account_holder_name: string;
  payout_method: string;
  bank_name: string;
  bank_branch: string;
  bank_account_number: string;
  mobile_wallet_number: string;
  autopay_enabled: boolean;
}

export interface EmployeeManagementCardProps {
  form: EmployeeFormState;
  setForm: Dispatch<SetStateAction<EmployeeFormState>>;
  updateField: (field: keyof EmployeeFormState, value: string | boolean) => void;
  positions: PeoplePosition[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  importFile: File | null;
  onImportFileChange: (file: File | null) => void;
  onBulkImport: () => void;
  isImporting: boolean;
}

export const EmployeeManagementCard: FC<EmployeeManagementCardProps> = ({
  form,
  setForm,
  updateField,
  positions,
  isSubmitting,
  onSubmit,
  importFile,
  onImportFileChange,
  onBulkImport,
  isImporting,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('employeeManagement')}</h2>

      <label>
        {t('fullName')}
        <input value={form.full_name} onChange={(event) => updateField('full_name', event.target.value)} />
      </label>

      <div className="split">
        <label>
          {t('employeeCode')}
          <input value={form.employee_code} onChange={(event) => updateField('employee_code', event.target.value)} />
        </label>
        <label>
          {t('basicSalary')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.basic_salary}
            onChange={(event) => updateField('basic_salary', event.target.value)}
          />
        </label>
      </div>

      <div className="split split-three">
        <label>
          {t('taxableAllowance')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.taxable_allowance}
            onChange={(event) => updateField('taxable_allowance', event.target.value)}
          />
        </label>
        <label>
          {t('nonTaxableAllowance')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.non_taxable_allowance}
            onChange={(event) => updateField('non_taxable_allowance', event.target.value)}
          />
        </label>
        <label>
          {t('otherDeductions')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.other_deductions}
            onChange={(event) => updateField('other_deductions', event.target.value)}
          />
        </label>
      </div>

      <div className="split">
        <label>
          {t('department')}
          <input value={form.department} onChange={(event) => updateField('department', event.target.value)} />
        </label>
        <label>
          {t('position')}
          <input value={form.position} onChange={(event) => updateField('position', event.target.value)} />
        </label>
      </div>

      {positions.length ? (
        <label>
          {t('managedPosition')}
          <select
            value={form.position_id}
            onChange={(event) => {
              const nextId = event.target.value;
              const nextPosition = positions.find((item) => item.id === Number(nextId));
              setForm((current) => ({
                ...current,
                position_id: nextId,
                position: nextPosition?.title || current.position,
                department: nextPosition?.department_name || current.department,
              }));
            }}
          >
            <option value="">{t('selectPosition')}</option>
            {positions.map((position) => (
              <option key={position.id} value={position.id}>
                {position.title} ({position.job_code})
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label>
        {t('taxId')}
        <input value={form.tax_id} onChange={(event) => updateField('tax_id', event.target.value)} />
      </label>

      <label>
        {t('accountHolderName')}
        <input
          value={form.account_holder_name}
          onChange={(event) => updateField('account_holder_name', event.target.value)}
        />
      </label>

      <div className="split split-three">
        <label>
          {t('payoutMethod')}
          <select value={form.payout_method} onChange={(event) => updateField('payout_method', event.target.value)}>
            <option value="bank_transfer">{t('bankTransfer')}</option>
            <option value="mobile_money">{t('mobileMoney')}</option>
          </select>
        </label>
        <label>
          {t('bankName')}
          <input value={form.bank_name} onChange={(event) => updateField('bank_name', event.target.value)} />
        </label>
        <label>
          {t('bankBranch')}
          <input value={form.bank_branch} onChange={(event) => updateField('bank_branch', event.target.value)} />
        </label>
      </div>

      <div className="split">
        <label>
          {t('bankAccountNumber')}
          <input
            value={form.bank_account_number}
            onChange={(event) => updateField('bank_account_number', event.target.value)}
          />
        </label>
        <label>
          {t('mobileWalletNumber')}
          <input
            value={form.mobile_wallet_number}
            onChange={(event) => updateField('mobile_wallet_number', event.target.value)}
          />
        </label>
      </div>

      <label>
        <input
          type="checkbox"
          checked={Boolean(form.autopay_enabled)}
          onChange={(event) => updateField('autopay_enabled', event.target.checked)}
        />
        {' '}
        {t('autopayEnabled')}
      </label>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : t('addEmployee')}
      </button>
      <div className="import-block">
        <label>
          {t('bulkImport')}
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onImportFileChange(event.target.files?.[0] || null)
            }
          />
        </label>
        <button
          type="button"
          className="secondary-button"
          onClick={onBulkImport}
          disabled={isImporting || !importFile}
        >
          {isImporting ? t('importing') : t('bulkImport')}
        </button>
        <p className="helper">{t('importTemplateHint')}</p>
      </div>
    </form>
  );
};

export interface PositionFormState {
  job_code: string;
  title: string;
  department_id: string;
  grade: string;
  headcount: string | number;
  min_salary: string | number;
  max_salary: string | number;
}

export interface PositionManagementCardProps {
  form: PositionFormState;
  setForm: Dispatch<SetStateAction<PositionFormState>>;
  departments: PeopleDepartment[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export const PositionManagementCard: FC<PositionManagementCardProps> = ({
  form,
  setForm,
  departments,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('positionManagement')}</h2>

      <div className="split">
        <label>
          {t('jobCode')}
          <input
            value={form.job_code}
            onChange={(event) => setForm((current) => ({ ...current, job_code: event.target.value }))}
          />
        </label>
        <label>
          {t('position')}
          <input
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
      </div>

      <div className="split split-three">
        <label>
          {t('department')}
          <select
            value={form.department_id}
            onChange={(event) => setForm((current) => ({ ...current, department_id: event.target.value }))}
          >
            <option value="">{t('notAvailable')}</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('grade')}
          <input
            value={form.grade}
            onChange={(event) => setForm((current) => ({ ...current, grade: event.target.value }))}
          />
        </label>
        <label>
          {t('headcount')}
          <input
            type="number"
            min="1"
            value={form.headcount}
            onChange={(event) => setForm((current) => ({ ...current, headcount: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          {t('minSalary')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.min_salary}
            onChange={(event) => setForm((current) => ({ ...current, min_salary: event.target.value }))}
          />
        </label>
        <label>
          {t('maxSalary')}
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.max_salary}
            onChange={(event) => setForm((current) => ({ ...current, max_salary: event.target.value }))}
          />
        </label>
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : t('createPosition')}
      </button>
    </form>
  );
};

export interface PositionsListCardProps {
  positions: PeoplePosition[];
  activeLanguage: string;
}

export const PositionsListCard: FC<PositionsListCardProps> = ({ positions, activeLanguage }) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <div className="section-header">
        <h2>{t('positions')}</h2>
        <span className="helper">{positions.length}</span>
      </div>
      {positions.length ? (
        <ul className="list">
          {positions.map((position) => (
            <li key={position.id}>
              <div>
                <strong>{position.title}</strong>
                <span>
                  {position.job_code}
                  {position.grade ? ` • ${position.grade}` : ''}
                  {position.department_name ? ` • ${position.department_name}` : ''}
                </span>
                <span>
                  {formatCurrency(position.min_salary, activeLanguage)} to {formatCurrency(position.max_salary, activeLanguage)} • {position.filled_headcount}/{position.headcount}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noPositions')}</p>
      )}
    </article>
  );
};

export interface EmployeesListCardProps {
  employees: PeopleEmployee[];
  activeLanguage: string;
}

export const EmployeesListCard: FC<EmployeesListCardProps> = ({ employees, activeLanguage }) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <h2>{t('employees')}</h2>
      {employees.length ? (
        <ul className="list">
          {employees.map((employee) => (
            <li key={employee.id}>
              <div>
                <strong>{employee.full_name}</strong>
                <span>
                  {employee.employee_code} • {employee.department || t('general')} •{' '}
                  {formatCurrency(employee.basic_salary, activeLanguage)} {t('salary')} • {formatCurrency(employee.taxable_allowance, activeLanguage)} {t('taxableAllowance').toLowerCase()} •{' '}
                  {employee.payout_method === 'mobile_money' ? t('mobileMoney') : t('bankTransfer')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noEmployees')}</p>
      )}
    </article>
  );
};

export interface TrainingCourseFormState {
  title: string;
  category: string;
  description: string;
  required_by_default: boolean;
  due_days_after_assignment: string | number;
  renewal_months: string | number;
  is_active: boolean;
}

export interface TrainingAssignmentFormState {
  course_id: string;
  employee_id: string;
  due_date: string;
  notes: string;
}

export interface TrainingCourseItem {
  id: number | string;
  title: string;
  category: string;
  description?: string | null;
  required_by_default: boolean;
  due_days_after_assignment?: number | null;
  renewal_months?: number | null;
  is_active: boolean;
  assigned_count?: number;
  completed_count?: number;
  overdue_count?: number;
}

export interface TrainingAssignmentItem {
  id: number | string;
  course_title: string;
  course_category: string;
  employee_name: string;
  employee_code: string;
  status: string;
  due_date?: string | null;
  completed_at?: string | null;
}

export interface LearningComplianceCardProps {
  courseForm: TrainingCourseFormState;
  setCourseForm: Dispatch<SetStateAction<TrainingCourseFormState>>;
  assignmentForm: TrainingAssignmentFormState;
  setAssignmentForm: Dispatch<SetStateAction<TrainingAssignmentFormState>>;
  courses: TrainingCourseItem[];
  assignments: TrainingAssignmentItem[];
  employees: PeopleEmployee[];
  isCourseSubmitting: boolean;
  isAssignmentSubmitting: boolean;
  onCourseSubmit: FormEventHandler<HTMLFormElement>;
  onAssignmentSubmit: FormEventHandler<HTMLFormElement>;
}

export const LearningComplianceCard: FC<LearningComplianceCardProps> = ({
  courseForm,
  setCourseForm,
  assignmentForm,
  setAssignmentForm,
  courses,
  assignments,
  employees,
  isCourseSubmitting,
  isAssignmentSubmitting,
  onCourseSubmit,
  onAssignmentSubmit,
}) => (
  <article className="card list-card">
    <div className="section-header">
      <h2>Learning and Compliance</h2>
      <span className="helper">{assignments.filter((item) => item.status !== 'completed').length} open</span>
    </div>
    <form className="subcard" onSubmit={onCourseSubmit}>
      <h3>Training course</h3>
      <div className="split">
        <label>
          Title
          <input value={courseForm.title} onChange={(event) => setCourseForm((current) => ({ ...current, title: event.target.value }))} />
        </label>
        <label>
          Category
          <input value={courseForm.category} onChange={(event) => setCourseForm((current) => ({ ...current, category: event.target.value }))} />
        </label>
      </div>
      <label>
        Description
        <textarea value={courseForm.description} onChange={(event) => setCourseForm((current) => ({ ...current, description: event.target.value }))} />
      </label>
      <div className="split split-three">
        <label>
          Due days
          <input type="number" min="0" value={courseForm.due_days_after_assignment} onChange={(event) => setCourseForm((current) => ({ ...current, due_days_after_assignment: event.target.value }))} />
        </label>
        <label>
          Renewal months
          <input type="number" min="1" value={courseForm.renewal_months} onChange={(event) => setCourseForm((current) => ({ ...current, renewal_months: event.target.value }))} />
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={courseForm.required_by_default} onChange={(event) => setCourseForm((current) => ({ ...current, required_by_default: event.target.checked }))} />
          <span>Required</span>
        </label>
      </div>
      <button type="submit" disabled={isCourseSubmitting}>{isCourseSubmitting ? 'Saving...' : 'Create course'}</button>
    </form>
    <form className="subcard" onSubmit={onAssignmentSubmit}>
      <h3>Assign training</h3>
      <div className="split split-three">
        <label>
          Course
          <select value={assignmentForm.course_id} onChange={(event) => setAssignmentForm((current) => ({ ...current, course_id: event.target.value }))}>
            <option value="">Select course</option>
            {courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
          </select>
        </label>
        <label>
          Employee
          <select value={assignmentForm.employee_id} onChange={(event) => setAssignmentForm((current) => ({ ...current, employee_id: event.target.value }))}>
            <option value="">Select employee</option>
            {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_code})</option>)}
          </select>
        </label>
        <label>
          Due date
          <input value={assignmentForm.due_date} onChange={(event) => setAssignmentForm((current) => ({ ...current, due_date: event.target.value }))} />
        </label>
      </div>
      <label>
        Notes
        <input value={assignmentForm.notes} onChange={(event) => setAssignmentForm((current) => ({ ...current, notes: event.target.value }))} />
      </label>
      <button type="submit" disabled={isAssignmentSubmitting}>{isAssignmentSubmitting ? 'Assigning...' : 'Assign training'}</button>
    </form>
    {courses.length ? (
      <ul className="list compact-list">
        {courses.map((course) => (
          <li key={course.id}>
            <div>
              <strong>{course.title}</strong>
              <span>{course.category} • {course.is_active ? 'active' : 'inactive'} • assigned {course.assigned_count || 0} • completed {course.completed_count || 0} • overdue {course.overdue_count || 0}</span>
              {course.description ? <span>{course.description}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    ) : <p>No training courses configured yet.</p>}
    <h3>Assignments</h3>
    {assignments.length ? (
      <ul className="list compact-list">
        {assignments.slice(0, 10).map((assignment) => (
          <li key={assignment.id}>
            <div>
              <strong>{assignment.course_title}</strong>
              <span>{assignment.employee_name} ({assignment.employee_code}) • {assignment.status}{assignment.due_date ? ` • due ${assignment.due_date}` : ''}{assignment.completed_at ? ` • completed ${assignment.completed_at}` : ''}</span>
            </div>
          </li>
        ))}
      </ul>
    ) : <p>No training assignments yet.</p>}
  </article>
);

export interface EmployeeDocumentFormState {
  employee_id: string;
  document_name: string;
  category: string;
  issued_at: string;
  expires_at: string;
  requires_acknowledgement: boolean;
  acknowledgement_statement: string;
  acknowledgement_version: string;
  file: File | null;
}

export interface EmployeeDocumentItem {
  id: number | string;
  employee_name: string;
  employee_code: string;
  category: string;
  document_name: string;
  file_name: string;
  expires_at?: string | null;
  status: string;
  requires_acknowledgement: boolean;
  acknowledged_at?: string | null;
}

export interface EmployeeDocumentsCardProps {
  form: EmployeeDocumentFormState;
  setForm: Dispatch<SetStateAction<EmployeeDocumentFormState>>;
  documents: EmployeeDocumentItem[];
  employees: PeopleEmployee[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onDownload: (documentId: number | string, fileName: string) => void | Promise<void>;
}

export const EmployeeDocumentsCard: FC<EmployeeDocumentsCardProps> = ({
  form,
  setForm,
  documents,
  employees,
  isSubmitting,
  onSubmit,
  onDownload,
}) => (
  <article className="card list-card">
    <div className="section-header">
      <h2>Employee Records</h2>
      <span className="helper">{documents.length} documents</span>
    </div>
    <form className="subcard" onSubmit={onSubmit}>
      <div className="split">
        <label>
          Employee
          <select value={form.employee_id} onChange={(event) => setForm((current) => ({ ...current, employee_id: event.target.value }))}>
            <option value="">Select employee</option>
            {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_code})</option>)}
          </select>
        </label>
        <label>
          Document name
          <input value={form.document_name} onChange={(event) => setForm((current) => ({ ...current, document_name: event.target.value }))} />
        </label>
      </div>
      <div className="split split-three">
        <label>
          Category
          <input value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} />
        </label>
        <label>
          Issued date
          <input value={form.issued_at} onChange={(event) => setForm((current) => ({ ...current, issued_at: event.target.value }))} />
        </label>
        <label>
          Expiry date
          <input value={form.expires_at} onChange={(event) => setForm((current) => ({ ...current, expires_at: event.target.value }))} />
        </label>
      </div>
      <label>
        File
        <input type="file" onChange={(event) => setForm((current) => ({ ...current, file: event.target.files?.[0] || null }))} />
      </label>
      <label className="checkbox-row">
        <input type="checkbox" checked={form.requires_acknowledgement} onChange={(event) => setForm((current) => ({ ...current, requires_acknowledgement: event.target.checked }))} />
        <span>Requires employee acknowledgement</span>
      </label>
      {form.requires_acknowledgement ? (
        <div className="split">
          <label>
            Acknowledgement version
            <input value={form.acknowledgement_version} onChange={(event) => setForm((current) => ({ ...current, acknowledgement_version: event.target.value }))} />
          </label>
          <label>
            Acknowledgement statement
            <input value={form.acknowledgement_statement} onChange={(event) => setForm((current) => ({ ...current, acknowledgement_statement: event.target.value }))} />
          </label>
        </div>
      ) : null}
      <button type="submit" disabled={isSubmitting || !form.file}>{isSubmitting ? 'Uploading...' : 'Upload record'}</button>
    </form>
    {documents.length ? (
      <ul className="list compact-list">
        {documents.map((document) => (
          <li key={document.id}>
            <div>
              <strong>{document.document_name}</strong>
              <span>{document.employee_name} ({document.employee_code}) • {document.category} • {document.status}{document.expires_at ? ` • expires ${document.expires_at}` : ''}{document.requires_acknowledgement ? ` • ${document.acknowledged_at ? 'acknowledged' : 'pending acknowledgement'}` : ''}</span>
              <span>{document.file_name}</span>
            </div>
            <div className="button-row">
              <button type="button" className="secondary-button" onClick={() => void onDownload(document.id, document.file_name)}>Download</button>
            </div>
          </li>
        ))}
      </ul>
    ) : <p>No employee records uploaded yet.</p>}
  </article>
);

export interface TimesheetEmployeeOption {
  id: number | string;
  full_name: string;
  employee_code: string;
}

export interface TimesheetsCardProps {
  timesheets: PeopleTimesheet[];
  employees: TimesheetEmployeeOption[];
  employeeId: string;
  onEmployeeIdChange: (id: string) => void;
  importFile: File | null;
  onImportFileChange: (file: File | null) => void;
  onImport: () => void;
  isImporting: boolean;
}

export const TimesheetsCard: FC<TimesheetsCardProps> = ({
  timesheets,
  employees,
  employeeId,
  onEmployeeIdChange,
  importFile,
  onImportFileChange,
  onImport,
  isImporting,
}) => {
  const { t } = useTranslation();

  return (
    <article className="card list-card">
      <div className="section-header">
        <h2>{t('timesheets')}</h2>
        <span className="helper">{timesheets.length}</span>
      </div>
      <label>
        {t('employee')}
        <select value={employeeId} onChange={(event) => onEmployeeIdChange(event.target.value)}>
          <option value="">{t('selectEmployee')}</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.full_name} ({employee.employee_code})
            </option>
          ))}
        </select>
      </label>
      <div className="import-block">
        <label>
          {t('importTimesheets')}
          <input
            type="file"
            accept=".csv"
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onImportFileChange(event.target.files?.[0] || null)
            }
          />
        </label>
        <button
          type="button"
          className="secondary-button"
          onClick={onImport}
          disabled={isImporting || !importFile}
        >
          {isImporting ? t('importing') : t('importTimesheets')}
        </button>
        <p className="helper">{t('importTimesheetHint')}</p>
      </div>
      {timesheets.length ? (
        <ul className="list compact-list">
          {timesheets.map((timesheet) => (
            <li key={timesheet.id}>
              <div>
                <strong>{timesheet.work_date}</strong>
                <span>{timesheet.regular_hours}h • {timesheet.overtime_hours}h OT • {timesheet.status}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noTimesheets')}</p>
      )}
    </article>
  );
};

export interface PerformanceCycleFormState {
  name: string;
  period_start: string;
  period_end: string;
  status: string;
}

export interface PeoplePerformanceCycle {
  id: number | string;
  name: string;
  period_start: string;
  period_end: string;
  status: string;
}

export interface PerformanceCyclesCardProps {
  form: PerformanceCycleFormState;
  setForm: Dispatch<SetStateAction<PerformanceCycleFormState>>;
  cycles: PeoplePerformanceCycle[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export const PerformanceCyclesCard: FC<PerformanceCyclesCardProps> = ({
  form,
  setForm,
  cycles,
  isSubmitting,
  onSubmit,
}) => (
  <article className="card list-card">
    <h2>Performance Cycles</h2>
    <form className="subcard" onSubmit={onSubmit}>
      <label>
        Cycle name
        <input
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        />
      </label>
      <div className="split split-three">
        <label>
          Start
          <input
            value={form.period_start}
            onChange={(event) => setForm((current) => ({ ...current, period_start: event.target.value }))}
          />
        </label>
        <label>
          End
          <input
            value={form.period_end}
            onChange={(event) => setForm((current) => ({ ...current, period_end: event.target.value }))}
          />
        </label>
        <label>
          Status
          <select
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>
      <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Create cycle'}</button>
    </form>
    {cycles.length ? (
      <ul className="list compact-list">
        {cycles.map((cycle) => (
          <li key={cycle.id}>
            <div>
              <strong>{cycle.name}</strong>
              <span>{cycle.period_start} to {cycle.period_end} • {cycle.status}</span>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No performance cycles configured yet.</p>
    )}
  </article>
);
